/**
 * Frontend Analytics Service
 * Tracks first-party events and bridges consented traffic to GA4 + Clarity.
 */

import { run as runCookieConsent } from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import '../styles/analyticsConsent.css';
import Clarity from '@microsoft/clarity';

const SESSION_ID_KEY = 'lilycove_analytics_session_id';
const ANONYMOUS_ID_KEY = 'lilycove_analytics_anonymous_id';
const SESSION_STARTED_SENT_KEY = 'lilycove_analytics_session_started_emitted';
const FIRST_TOUCH_KEY = 'lilycove_analytics_first_touch';
const LAST_TOUCH_KEY = 'lilycove_analytics_last_touch';
const CONSENT_STATE_KEY = 'lilycove_analytics_consent_state';

class FrontendAnalyticsService {
  constructor() {
    this.apiBaseUrl = process.env.REACT_APP_API_URL || '';
    this.sessionId = this.getOrCreateSessionId();
    this.anonymousId = this.getOrCreateAnonymousId();
    this.platform = 'web';
    this.userId = null;
    this.lastRoute = null;

    this.gaMeasurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID || '';
    this.clarityProjectId = process.env.REACT_APP_CLARITY_PROJECT_ID || '';
    this.gaManualPageviews = this.parseBooleanEnv(process.env.REACT_APP_GA4_MANUAL_PAGEVIEWS, false);
    this.gaRouteDebug = this.parseBooleanEnv(
      process.env.REACT_APP_GA4_ROUTE_DEBUG,
      process.env.NODE_ENV !== 'production'
    );
    this.gaInitialized = false;
    this.clarityInitialized = false;
    this.cookieConsentInitialized = false;
    this.lastAppliedConsentState = null;

    this.deterministicViewTrackingEnabled = this.parseBooleanEnv(
      process.env.REACT_APP_ANALYTICS_DETERMINISTIC_VIEWS,
      false
    );

    this.pageEntryTimes = new Map();
    this.sentViewExitKeys = new Set();
    this.lastExitFlushAt = 0;
    this.requestCounters = {
      standard: { success: 0, failure: 0 },
      unload: { success: 0, failure: 0 }
    };

    this.captureAttributionTouches();
    this.trackSessionStarted();

    if (this.deterministicViewTrackingEnabled) {
      window.addEventListener('beforeunload', () => {
        this.flushActiveViewExits(true);
      });
      window.addEventListener('pagehide', () => {
        this.flushActiveViewExits(true);
      });
    } else {
      window.addEventListener('beforeunload', () => {
        this.trackPageExits();
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.trackPageExits();
        }
      });
      window.addEventListener('pagehide', () => {
        this.trackPageExits();
      });
    }
  }

  parseBooleanEnv(value, fallback = false) {
    if (value === undefined || value === null) return fallback;
    return ['1', 'true', 'yes', 'on'].includes(String(value).toLowerCase());
  }

  canUseStorage(type = 'session') {
    try {
      if (typeof window === 'undefined') return false;
      const storage = type === 'local' ? window.localStorage : window.sessionStorage;
      const testKey = `__lilycove_analytics_${type}`;
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);
      return true;
    } catch (_error) {
      return false;
    }
  }

  generateSessionId() {
    return 'web_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  generateAnonymousId() {
    return 'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 12);
  }

  getOrCreateSessionId() {
    try {
      if (!this.canUseStorage('session')) {
        return this.generateSessionId();
      }

      const existing = sessionStorage.getItem(SESSION_ID_KEY);
      if (existing) {
        return existing;
      }

      const created = this.generateSessionId();
      sessionStorage.setItem(SESSION_ID_KEY, created);
      return created;
    } catch (_error) {
      return this.generateSessionId();
    }
  }

  getOrCreateAnonymousId() {
    try {
      if (!this.canUseStorage('local')) {
        return this.generateAnonymousId();
      }

      const existing = localStorage.getItem(ANONYMOUS_ID_KEY);
      if (existing) {
        return existing;
      }

      const created = this.generateAnonymousId();
      localStorage.setItem(ANONYMOUS_ID_KEY, created);
      return created;
    } catch (_error) {
      return this.generateAnonymousId();
    }
  }

  getStoredConsentState() {
    try {
      if (!this.canUseStorage('local')) return null;
      return localStorage.getItem(CONSENT_STATE_KEY);
    } catch (_error) {
      return null;
    }
  }

  hasAnalyticsConsent() {
    return this.getStoredConsentState() === 'accepted';
  }

  setStoredConsentState(state) {
    try {
      if (!this.canUseStorage('local')) return;
      localStorage.setItem(CONSENT_STATE_KEY, state);
    } catch (_error) {
      // No-op
    }
  }

  getCurrentPath() {
    if (typeof window === 'undefined') return null;
    return `${window.location.pathname}${window.location.search}`;
  }

  parseUtmFromSearch(search = '') {
    const params = new URLSearchParams(search || '');
    return {
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null,
      utm_term: params.get('utm_term') || null,
      utm_content: params.get('utm_content') || null
    };
  }

  buildTouchPayload() {
    if (typeof window === 'undefined') return null;

    const utm = this.parseUtmFromSearch(window.location.search);
    const hasUtm = Object.values(utm).some(Boolean);
    const referrer = document.referrer || null;

    if (!hasUtm && !referrer) {
      return null;
    }

    return {
      ...utm,
      referrer,
      landing_path: `${window.location.pathname}${window.location.search}`,
      captured_at_utc: new Date().toISOString()
    };
  }

  readTouch(storageType, key) {
    try {
      const storage = storageType === 'local' ? localStorage : sessionStorage;
      const raw = storage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (_error) {
      return null;
    }
  }

  writeTouch(storageType, key, value) {
    try {
      const storage = storageType === 'local' ? localStorage : sessionStorage;
      storage.setItem(key, JSON.stringify(value));
    } catch (_error) {
      // No-op
    }
  }

  captureAttributionTouches() {
    if (typeof window === 'undefined') return;

    const touch = this.buildTouchPayload();
    if (!touch) return;

    const hasLocalStorage = this.canUseStorage('local');
    const hasSessionStorage = this.canUseStorage('session');

    if (hasLocalStorage) {
      const firstTouch = this.readTouch('local', FIRST_TOUCH_KEY);
      if (!firstTouch) {
        this.writeTouch('local', FIRST_TOUCH_KEY, touch);
      }
    }

    if (hasSessionStorage) {
      this.writeTouch('session', LAST_TOUCH_KEY, touch);
    }
  }

  getAttributionPayload() {
    const firstTouch = this.canUseStorage('local') ? this.readTouch('local', FIRST_TOUCH_KEY) : null;
    const lastTouch = this.canUseStorage('session') ? this.readTouch('session', LAST_TOUCH_KEY) : null;

    return {
      utm_source: lastTouch?.utm_source || firstTouch?.utm_source || null,
      utm_medium: lastTouch?.utm_medium || firstTouch?.utm_medium || null,
      utm_campaign: lastTouch?.utm_campaign || firstTouch?.utm_campaign || null,
      utm_term: lastTouch?.utm_term || firstTouch?.utm_term || null,
      utm_content: lastTouch?.utm_content || firstTouch?.utm_content || null,
      first_touch_source: firstTouch?.utm_source || null,
      first_touch_medium: firstTouch?.utm_medium || null,
      last_touch_source: lastTouch?.utm_source || null,
      last_touch_medium: lastTouch?.utm_medium || null
    };
  }

  trackSessionStarted() {
    const alreadySent = this.canUseStorage('session')
      ? sessionStorage.getItem(SESSION_STARTED_SENT_KEY) === this.sessionId
      : false;

    if (alreadySent) {
      return;
    }

    if (this.canUseStorage('session')) {
      sessionStorage.setItem(SESSION_STARTED_SENT_KEY, this.sessionId);
    }

    this.trackCanonicalEvent('session_started', {
      session_started_at_utc: new Date().toISOString()
    }).catch(() => null);
  }

  initConsentManager() {
    if (typeof window === 'undefined' || this.cookieConsentInitialized) {
      return;
    }

    this.cookieConsentInitialized = true;
    this.setGtagConsentDefault();

    runCookieConsent({
      // Keep site interaction enabled before consent so the banner is non-blocking.
      disablePageInteraction: false,
      guiOptions: {
        consentModal: {
          layout: 'box',
          position: 'bottom right',
          equalWeightButtons: true,
          flipButtons: false
        },
        preferencesModal: {
          layout: 'box',
          position: 'right',
          equalWeightButtons: true,
          flipButtons: false
        }
      },
      categories: {
        necessary: {
          enabled: true,
          readOnly: true
        },
        analytics: {
          enabled: false,
          readOnly: false
        }
      },
      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              title: '<div class="lc-consent-title-wrap"><img src="/logo.png" alt="Lilycove logo" class="lc-consent-logo" width="24" height="24" /><span>Analytics Consent</span></div>',
              description: "We use analytics to see what's working, catch bugs, and improve the search experience for the whole community. You can opt out anytime.",
              acceptAllBtn: 'Accept',
              acceptNecessaryBtn: 'Decline'
            },
            preferencesModal: {
              title: 'Cookie Preferences',
              acceptAllBtn: 'Accept',
              acceptNecessaryBtn: 'Decline',
              savePreferencesBtn: 'Save',
              closeIconLabel: 'Close',
              sections: []
            }
          }
        }
      },
      onFirstConsent: ({ cookie }) => {
        this.handleConsentUpdate(Boolean(cookie?.categories?.includes('analytics')));
      },
      onConsent: ({ cookie }) => {
        this.handleConsentUpdate(Boolean(cookie?.categories?.includes('analytics')));
      }
    });

    if (this.hasAnalyticsConsent()) {
      this.handleConsentUpdate(true);
    }
  }

  handleConsentUpdate(granted) {
    if (this.lastAppliedConsentState === granted) {
      return;
    }
    this.lastAppliedConsentState = granted;

    if (granted) {
      this.setStoredConsentState('accepted');
      this.updateGtagConsent('granted');
      this.initializeGa4();
      this.initializeClarity();
      return;
    }

    this.setStoredConsentState('declined');
    this.updateGtagConsent('denied');
  }

  getGtag() {
    if (typeof window === 'undefined') return null;
    return typeof window.gtag === 'function' ? window.gtag : null;
  }

  setGtagConsentDefault() {
    const gtag = this.getGtag();
    if (!gtag) return;

    gtag('consent', 'default', {
      analytics_storage: 'denied'
    });
  }

  updateGtagConsent(state) {
    const gtag = this.getGtag();
    if (!gtag) return;

    gtag('consent', 'update', {
      analytics_storage: state
    });
  }

  initializeGa4() {
    if (this.gaInitialized || !this.hasAnalyticsConsent() || !this.gaMeasurementId) {
      return;
    }

    const gtag = this.getGtag();
    if (!gtag) return;

    gtag('js', new Date());
    gtag('config', this.gaMeasurementId, {
      send_page_view: !this.gaManualPageviews,
      debug_mode: process.env.NODE_ENV !== 'production'
    });

    this.gaInitialized = true;

    if (this.gaManualPageviews) {
      const currentPath = this.getCurrentPath();
      if (currentPath) {
        this.emitGaEvent('page_view', {
          page_path: currentPath
        });
      }
    }
  }

  initializeClarity() {
    if (this.clarityInitialized || !this.hasAnalyticsConsent() || !this.clarityProjectId) {
      return;
    }

    try {
      Clarity.init(this.clarityProjectId);
      this.clarityInitialized = true;
    } catch (error) {
      console.warn('Clarity init failed:', error.message);
    }
  }

  emitGaEvent(eventName, params = {}) {
    if (!this.hasAnalyticsConsent()) {
      return;
    }

    this.initializeGa4();

    const gtag = this.getGtag();
    if (!gtag || !this.gaInitialized) {
      return;
    }

    const eventParams = {
      session_id: this.sessionId,
      anonymous_id: this.anonymousId,
      user_id: this.userId || undefined,
      ...this.getAttributionPayload(),
      ...params
    };

    gtag('event', eventName, this.cleanParams(eventParams));
  }

  normalizeEventPayload(data = {}) {
    return this.cleanParams({
      event_time_utc: new Date().toISOString(),
      event_name: data.event_name,
      event_id: data.event_id || `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
      anonymous_id: this.anonymousId,
      session_id: this.sessionId,
      user_id: this.userId,
      platform: this.platform,
      page_path: typeof window !== 'undefined' ? window.location.pathname : null,
      page_url: typeof window !== 'undefined' ? window.location.href : null,
      referrer: typeof document !== 'undefined' ? document.referrer : null,
      ...this.getAttributionPayload(),
      ...data
    });
  }

  async trackCanonicalEvent(eventName, properties = {}) {
    const payload = this.normalizeEventPayload({
      event_name: eventName,
      properties
    });

    try {
      const response = await fetch(`${this.apiBaseUrl}/api/analytics/events`, {
        method: 'POST',
        headers: this.buildHeaders(true),
        body: JSON.stringify({
          events: [payload]
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      this.incrementCounter('standard', true);
      return await response.json();
    } catch (error) {
      console.warn('Canonical analytics tracking failed:', error.message);
      this.incrementCounter('standard', false);
      return null;
    }
  }

  setUserId(userId, options = {}) {
    const { emitIdentityLink = true, linkReason = 'session_authenticated' } = options;
    const previousUserId = this.userId;
    this.userId = userId;

    if (emitIdentityLink && !previousUserId && userId) {
      this.trackIdentityLinked(userId, linkReason).catch(() => null);
    }
  }

  startTimer(key) {
    this.pageEntryTimes.set(key, Date.now());
    this.sentViewExitKeys.delete(key);
  }

  getTimerDuration(key) {
    const startTime = this.pageEntryTimes.get(key);
    if (!startTime) return 0;
    return Date.now() - startTime;
  }

  cleanParams(params = {}) {
    const cleaned = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        cleaned[key] = value;
      }
    });
    return cleaned;
  }

  buildQueryParams(params = {}) {
    return new URLSearchParams({
      ...this.cleanParams(params),
      ...this.getAttributionPayload(),
      session_id: this.sessionId,
      anonymous_id: this.anonymousId,
      platform: this.platform,
      ...(this.userId ? { user_id: this.userId } : {})
    });
  }

  buildHeaders(includeJson = false) {
    const headers = {
      'X-Platform': this.platform,
      'X-Session-ID': this.sessionId,
      'X-Anonymous-ID': this.anonymousId
    };

    if (includeJson) {
      headers['Content-Type'] = 'application/json';
    }

    if (this.userId) {
      headers['X-User-ID'] = this.userId;
    }

    return headers;
  }

  incrementCounter(kind, success) {
    const bucket = this.requestCounters[kind] || this.requestCounters.standard;
    if (success) {
      bucket.success += 1;
    } else {
      bucket.failure += 1;
    }
  }

  shouldThrottleExitFlush() {
    const now = Date.now();
    if ((now - this.lastExitFlushAt) < 400) {
      return true;
    }
    this.lastExitFlushAt = now;
    return false;
  }

  trackPageExits() {
    if (this.shouldThrottleExitFlush()) {
      return;
    }

    this.pageEntryTimes.forEach((startTime, key) => {
      const duration = Date.now() - startTime;
      if (duration > 1000) {
        if (key.startsWith('card_')) {
          const cardId = key.replace('card_', '');
          this.trackEventQuery('track/card-view', {
            card_id: cardId,
            came_from: 'page_exit',
            view_duration_ms: duration
          }, { useUnloadTransport: true, transport: 'unload' });
          this.sentViewExitKeys.add(key);
        } else if (key.startsWith('set_')) {
          const setId = key.replace('set_', '');
          this.trackEventQuery('track/set-view', {
            set_id: setId,
            came_from: 'page_exit',
            view_duration_ms: duration
          }, { useUnloadTransport: true, transport: 'unload' });
          this.sentViewExitKeys.add(key);
        } else if (key.startsWith('series_')) {
          const seriesId = key.replace('series_', '');
          this.trackEventQuery('track/series-view', {
            series_id: seriesId,
            view_duration_ms: duration
          }, { useUnloadTransport: true, transport: 'unload' });
          this.sentViewExitKeys.add(key);
        }
      }
    });

    this.pageEntryTimes.clear();
  }

  flushActiveViewExits(useUnloadTransport = true) {
    if (this.shouldThrottleExitFlush()) {
      return;
    }

    this.pageEntryTimes.forEach((startTime, key) => {
      const duration = Date.now() - startTime;
      if (duration <= 1000 || this.sentViewExitKeys.has(key)) {
        return;
      }

      if (key.startsWith('card_')) {
        const cardId = key.replace('card_', '');
        this.trackEventQuery('track/card-view', {
          card_id: cardId,
          came_from: 'page_exit',
          view_duration_ms: duration
        }, { useUnloadTransport, transport: 'unload' });
      } else if (key.startsWith('set_')) {
        const setId = key.replace('set_', '');
        this.trackEventQuery('track/set-view', {
          set_id: setId,
          came_from: 'page_exit',
          view_duration_ms: duration
        }, { useUnloadTransport, transport: 'unload' });
      } else if (key.startsWith('series_')) {
        const seriesId = key.replace('series_', '');
        this.trackEventQuery('track/series-view', {
          series_id: seriesId,
          view_duration_ms: duration
        }, { useUnloadTransport, transport: 'unload' });
      }

      this.sentViewExitKeys.add(key);
      this.pageEntryTimes.delete(key);
    });
  }

  async trackEvent(endpoint, data = {}) {
    try {
      const headers = this.buildHeaders(true);

      const response = await fetch(`${this.apiBaseUrl}/api/analytics/${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...data,
          ...this.getAttributionPayload(),
          anonymous_id: this.anonymousId,
          session_id: this.sessionId,
          platform: this.platform,
          timestamp: new Date().toISOString(),
          ...(this.userId ? { user_id: this.userId } : {})
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      this.incrementCounter('standard', true);
      return await response.json();
    } catch (error) {
      console.warn('Analytics tracking failed:', error.message);
      this.incrementCounter('standard', false);
      return null;
    }
  }

  async trackEventQuery(endpoint, params = {}, options = {}) {
    const { useUnloadTransport = false, transport = 'default' } = options;
    const allParams = { ...params, transport };

    if (useUnloadTransport) {
      return this.trackEventQueryUnload(endpoint, allParams);
    }

    try {
      const queryParams = this.buildQueryParams(allParams);
      const headers = this.buildHeaders(false);

      const response = await fetch(`${this.apiBaseUrl}/api/analytics/${endpoint}?${queryParams}`, {
        method: 'POST',
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      this.incrementCounter('standard', true);
      return await response.json();
    } catch (error) {
      console.warn('Analytics tracking failed:', error.message);
      this.incrementCounter('standard', false);
      return null;
    }
  }

  async trackEventQueryUnload(endpoint, params = {}) {
    const queryParams = this.buildQueryParams(params);
    const url = `${this.apiBaseUrl}/api/analytics/${endpoint}?${queryParams}`;

    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const queued = navigator.sendBeacon(url, '');
        this.incrementCounter('unload', queued);
        if (queued) {
          return { success: true, queued: true, transport: 'beacon' };
        }
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: this.buildHeaders(false),
        keepalive: true
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      this.incrementCounter('unload', true);
      try {
        return await response.json();
      } catch (_error) {
        return { success: true, transport: 'keepalive' };
      }
    } catch (error) {
      console.warn('Analytics unload tracking failed:', error.message);
      this.incrementCounter('unload', false);
      return null;
    }
  }

  trackRouteChange(pathname, search = '') {
    const route = `${pathname || ''}${search || ''}`;
    if (!route || route === this.lastRoute) {
      return;
    }

    this.captureAttributionTouches();

    const previousRoute = this.lastRoute;
    this.lastRoute = route;

    if (this.gaRouteDebug) {
      console.info('[analytics][ga4] route change', {
        route,
        previousRoute,
        browserRouter: true,
        enhancedMeasurementCheckRequired: true,
        manualPageviewsEnabled: this.gaManualPageviews
      });
    }

    this.trackPageView(route, previousRoute || (typeof document !== 'undefined' ? document.referrer : null));

    if (this.gaManualPageviews) {
      this.emitGaEvent('page_view', {
        page_path: route,
        page_location: typeof window !== 'undefined' ? window.location.href : undefined,
        page_referrer: previousRoute || (typeof document !== 'undefined' ? document.referrer : undefined)
      });
    }
  }

  async trackSearch(query, resultsCount = 0, metadata = {}) {
    await this.trackSearchSubmitted(query, metadata);
    return this.trackSearchResultsReturned(query, resultsCount, metadata);
  }

  async trackSearchSubmitted(query, metadata = {}) {
    const payload = {
      query,
      results_count: metadata.results_count || 0,
      search_time_ms: metadata.search_time_ms || 0,
      page: metadata.page || 1,
      source_page: metadata.source_page || 'search',
      ...metadata
    };

    this.emitGaEvent('search_submitted', {
      search_query: query,
      result_count: metadata.results_count,
      page_path: this.getCurrentPath()
    });

    const canonicalResult = await this.trackCanonicalEvent('search_submitted', payload);
    if (canonicalResult) {
      return canonicalResult;
    }

    // Legacy fallback to avoid dropping search tracking during transition.
    return this.trackEventQuery('track/search', {
      query,
      results_count: metadata.results_count || 0,
      search_time_ms: metadata.search_time_ms || 0
    });
  }

  async trackSearchResultsReturned(query, resultsCount = 0, metadata = {}) {
    const payload = {
      query,
      results_count: resultsCount,
      has_results: Number(resultsCount) > 0,
      ...metadata
    };

    this.emitGaEvent('search_results_returned', {
      search_query: query,
      result_count: resultsCount,
      has_results: Number(resultsCount) > 0
    });

    return this.trackCanonicalEvent('search_results_returned', payload);
  }

  async trackSearchResultClicked(payload = {}) {
    const safePayload = {
      query: payload.query || null,
      card_id: payload.card_id || payload.cardId || null,
      position: payload.position || null,
      page: payload.page || null,
      result_count: payload.result_count || null,
      source_page: payload.source_page || 'search'
    };

    this.emitGaEvent('search_result_clicked', {
      search_query: safePayload.query,
      card_id: safePayload.card_id,
      result_position: safePayload.position,
      result_count: safePayload.result_count
    });

    await this.trackCanonicalEvent('search_result_clicked', safePayload);
  }

  async trackCardView(cardId, cameFrom = 'direct', setId = null, viewDurationMs = null) {
    this.startTimer(`card_${cardId}`);

    if (this.deterministicViewTrackingEnabled) {
      return Promise.resolve({ success: true, deferred: true });
    }

    const duration = viewDurationMs !== null ? viewDurationMs : 0;

    const payload = {
      card_id: cardId,
      came_from: cameFrom,
      set_id: setId,
      view_duration_ms: duration
    };

    const canonicalResult = await this.trackCanonicalEvent('card_viewed', payload);
    if (canonicalResult) {
      return canonicalResult;
    }

    return this.trackEventQuery('track/card-view', payload);
  }

  async trackSetView(setId, viewDurationMs = null, cameFrom = 'direct') {
    if (this.deterministicViewTrackingEnabled) {
      this.startTimer(`set_${setId}`);
      return Promise.resolve({ success: true, deferred: true });
    }

    const sessionKey = `set_viewed_${setId}`;
    const lastViewed = sessionStorage.getItem(sessionKey);
    const now = Date.now();

    if (lastViewed && (now - parseInt(lastViewed, 10)) < 30000) {
      return null;
    }

    sessionStorage.setItem(sessionKey, now.toString());
    this.startTimer(`set_${setId}`);

    const duration = viewDurationMs !== null ? viewDurationMs : 0;

    return this.trackEventQuery('track/set-view', {
      set_id: setId,
      view_duration_ms: duration,
      came_from: cameFrom
    });
  }

  async trackSeriesView(seriesId, viewDurationMs = null) {
    this.startTimer(`series_${seriesId}`);

    if (this.deterministicViewTrackingEnabled) {
      return Promise.resolve({ success: true, deferred: true });
    }

    const duration = viewDurationMs !== null ? viewDurationMs : 0;

    return this.trackEventQuery('track/series-view', {
      series_id: seriesId,
      view_duration_ms: duration
    });
  }

  async trackSetNavigation(setId, seriesId = null, sourcePage = 'series_page') {
    return this.trackEventQuery('track/set-navigation', {
      set_id: setId,
      series_id: seriesId,
      source_page: sourcePage
    }, {
      useUnloadTransport: this.deterministicViewTrackingEnabled,
      transport: this.deterministicViewTrackingEnabled ? 'unload' : 'default'
    });
  }

  normalizeChannel(channel) {
    if (!channel) return 'unknown';
    const value = String(channel).toLowerCase();
    if (value.includes('ebay')) return 'ebay';
    if (value.includes('shopify') || value.includes('store')) return 'shopify';
    return value;
  }

  async trackMarketplaceClick(marketplace, cardId = null, listingId = null, priceCad = null, metadata = {}) {
    const safeMetadata = metadata || {};
    const channel = this.normalizeChannel(safeMetadata.channel || marketplace);
    const normalizedPayload = {
      marketplace,
      channel,
      store_id: safeMetadata.store_id || null,
      store_name: safeMetadata.store_name || null,
      card_id: cardId,
      listing_id: listingId || safeMetadata.listing_id,
      listing_url: safeMetadata.listing_url,
      price: priceCad,
      price_cad: priceCad,
      has_price: Number.isFinite(Number(priceCad)),
      display_currency: safeMetadata.display_currency,
      display_price: safeMetadata.display_price,
      condition: safeMetadata.condition
    };

    this.emitGaEvent('listing_clickout', {
      channel,
      store_id: normalizedPayload.store_id,
      store_name: normalizedPayload.store_name,
      listing_id: normalizedPayload.listing_id,
      card_id: cardId,
      price_cad: priceCad,
      has_price: normalizedPayload.has_price
    });

    const canonicalResult = await this.trackCanonicalEvent('listing_clickout', normalizedPayload);
    if (canonicalResult) {
      return canonicalResult;
    }

    // Legacy fallback to avoid dropping clickout tracking during transition.
    return this.trackEventQuery('track/marketplace-click', normalizedPayload);
  }

  async trackPageView(pageUrl = null, referrer = null) {
    const url = pageUrl || this.getCurrentPath();
    const ref = referrer || (typeof document !== 'undefined' ? document.referrer : null);

    const payload = {
      page_url: url,
      referrer: ref
    };

    const canonicalResult = await this.trackCanonicalEvent('page_viewed', payload);
    if (canonicalResult) {
      return canonicalResult;
    }

    return this.trackEventQuery('track/page-view', payload);
  }

  async trackFilter(filterType, filterValue) {
    return this.trackEventQuery('track/filter', {
      filter_type: filterType,
      filter_value: filterValue
    });
  }

  async trackAuthSignedUp(userId, metadata = {}) {
    this.setUserId(userId, { emitIdentityLink: true, linkReason: 'signup' });

    this.emitGaEvent('auth_signed_up', {
      auth_method: metadata.auth_method || metadata.provider || 'unknown',
      user_id: userId
    });

    await this.trackCanonicalEvent('auth_signed_up', {
      user_id: userId,
      ...metadata
    });

    return this.trackEventQuery('track/user-session', {
      action: 'registration'
    });
  }

  async trackAuthLoggedIn(userId, metadata = {}) {
    this.setUserId(userId, { emitIdentityLink: true, linkReason: 'login' });

    this.emitGaEvent('auth_logged_in', {
      auth_method: metadata.auth_method || metadata.provider || 'unknown',
      user_id: userId
    });

    await this.trackCanonicalEvent('auth_logged_in', {
      user_id: userId,
      ...metadata
    });

    return this.trackEventQuery('track/user-session', {
      action: 'login'
    });
  }

  async trackIdentityLinked(userId, linkReason = 'session_authenticated') {
    return this.trackCanonicalEvent('identity_linked', {
      anonymous_id: this.anonymousId,
      user_id: userId,
      link_reason: linkReason
    });
  }

  async trackUserAction(action, userId = null, metadata = {}) {
    if (!userId) return null;

    this.setUserId(userId);
    return this.trackEventQuery('track/authenticated-user', {
      event_type: action,
      metadata: JSON.stringify(metadata || {})
    });
  }

  trackNavigation(fromPage, toPage, element = null) {
    const navigationData = {
      from_page: fromPage,
      to_page: toPage,
      element_clicked: element,
      navigation_time: Date.now()
    };

    sessionStorage.setItem('navigation_context', JSON.stringify(navigationData));
  }

  getNavigationContext() {
    const context = sessionStorage.getItem('navigation_context');
    if (context) {
      sessionStorage.removeItem('navigation_context');
      return JSON.parse(context);
    }
    return null;
  }

  async trackSetViewExit(setId, cameFrom = 'direct') {
    const key = `set_${setId}`;
    if (this.sentViewExitKeys.has(key)) return null;

    const duration = this.getTimerDuration(key);
    if (duration <= 1000) return null;

    this.sentViewExitKeys.add(key);
    this.pageEntryTimes.delete(key);
    const isHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    return this.trackEventQuery('track/set-view', {
      set_id: setId,
      view_duration_ms: duration,
      came_from: cameFrom
    }, {
      useUnloadTransport: this.deterministicViewTrackingEnabled && isHidden,
      transport: isHidden ? 'unload' : 'default'
    });
  }

  async trackSeriesViewExit(seriesId) {
    const key = `series_${seriesId}`;
    if (this.sentViewExitKeys.has(key)) return null;

    const duration = this.getTimerDuration(key);
    if (duration <= 1000) return null;

    this.sentViewExitKeys.add(key);
    this.pageEntryTimes.delete(key);
    const isHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    return this.trackEventQuery('track/series-view', {
      series_id: seriesId,
      view_duration_ms: duration
    }, {
      useUnloadTransport: this.deterministicViewTrackingEnabled && isHidden,
      transport: isHidden ? 'unload' : 'default'
    });
  }

  async trackCardViewExit(cardId, setId = null, cameFrom = 'direct') {
    const key = `card_${cardId}`;
    if (this.sentViewExitKeys.has(key)) return null;

    const duration = this.getTimerDuration(key);
    if (duration <= 1000) return null;

    this.sentViewExitKeys.add(key);
    this.pageEntryTimes.delete(key);
    const isHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    return this.trackEventQuery('track/card-view', {
      card_id: cardId,
      set_id: setId,
      came_from: cameFrom,
      view_duration_ms: duration
    }, {
      useUnloadTransport: this.deterministicViewTrackingEnabled && isHidden,
      transport: isHidden ? 'unload' : 'default'
    });
  }
}

const analyticsService = new FrontendAnalyticsService();

export default analyticsService;
